import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard } from "lucide-react";
import { LOGO_WARZONE, LOGO_ALLIES } from "@/lib/api";
import CookieBanner from "@/components/CookieBanner";

const LINKS = [
  { to: "/seals-of-warzone", label: "Seals" },
  { to: "/seals-allies-force", label: "Seals Allies Force" },
  { to: "/tornei", label: "Tornei" },
  { to: "/campionati", label: "Campionati" },
  { to: "/storico-player", label: "Storico Player" },
  { to: "/contatti", label: "Contatti" },
];

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const allies = pathname.startsWith("/seals-allies-force");
  const accent = allies ? "text-[var(--gold-light)]" : "text-[#67e8f9]";
  const underline = allies ? "bg-[var(--gold)]" : "bg-[#22d3ee]";

  return (
    <div className="relative z-10 min-h-screen flex flex-col bg-[#050507]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/seals-of-warzone" className="flex items-center gap-3" data-testid="public-logo">
            <img src={LOGO_WARZONE} alt="Seals" className="w-10 h-10 object-contain" />
            <span className="heading text-2xl text-white tracking-wide hidden sm:block"><span className={accent}>SEALS</span></span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`navlink-${l.to.slice(1)}`}
                className={({ isActive }) => `relative font-rajdhani uppercase tracking-wider text-sm public-link ${isActive ? accent : "text-gray-300 hover:text-white"}`}>
                {({ isActive }) => (<>{l.label}<span className={`absolute -bottom-1.5 left-0 h-0.5 ${underline} transition-all ${isActive ? "w-full" : "w-0"}`} /></>)}
              </NavLink>
            ))}
            <a href="/dashboard" data-testid="dashboard-btn" className="btn-cyber clip-hud px-5 py-2 text-xs flex items-center gap-2"><LayoutDashboard className="w-4 h-4" /> Dashboard</a>
          </nav>

          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-white/10 bg-black/95 px-5 py-4 space-y-3 fade-up">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
                className={({ isActive }) => `block font-rajdhani uppercase tracking-wider text-sm ${isActive ? accent : "text-gray-300"}`}>{l.label}</NavLink>
            ))}
            <a href="/dashboard" className="btn-cyber clip-hud px-5 py-2 text-xs inline-flex items-center gap-2"><LayoutDashboard className="w-4 h-4" /> Dashboard</a>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-5 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img src={LOGO_WARZONE} alt="" className="w-11 h-11 object-contain" />
              <img src={LOGO_ALLIES} alt="" className="w-11 h-11 object-contain" />
            </div>
            <p className="font-rajdhani text-gray-400 text-sm">L'ecosistema competitivo ufficiale di Call of Duty: Warzone. Uniti sotto un'unica forza.</p>
          </div>
          <div>
            <h4 className="heading text-xl text-white mb-3">Navigazione</h4>
            {LINKS.map((l) => <Link key={l.to} to={l.to} className="block font-rajdhani text-gray-400 hover:text-[#67e8f9] text-sm py-0.5">{l.label}</Link>)}
          </div>
          <div>
            <h4 className="heading text-xl text-white mb-3">Community</h4>
            <a href="mailto:gestioneprivateseals@gmail.com" className="block font-rajdhani text-gray-400 hover:text-[#67e8f9] text-sm py-0.5">Email Staff</a>
            <a href="/dashboard" className="block font-rajdhani text-gray-400 hover:text-[#67e8f9] text-sm py-0.5">Dashboard</a>
            <Link to="/contatti" className="block font-rajdhani text-gray-400 hover:text-[#67e8f9] text-sm py-0.5">Contatti</Link>
          </div>
          <div>
            <h4 className="heading text-xl text-white mb-3">Legale</h4>
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="block font-rajdhani text-gray-400 hover:text-[#67e8f9] text-sm py-0.5">Privacy Policy</Link>
            <Link to="/cookie-policy" data-testid="footer-cookie-link" className="block font-rajdhani text-gray-400 hover:text-[#67e8f9] text-sm py-0.5">Cookie Policy</Link>
            <Link to="/termini" data-testid="footer-termini-link" className="block font-rajdhani text-gray-400 hover:text-[#67e8f9] text-sm py-0.5">Regolamento & Termini</Link>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center font-rajdhani text-gray-500 text-xs">
          © 2024 Seals · Seals Allies Force. Tutti i diritti riservati.
        </div>
      </footer>
      <CookieBanner />
    </div>
  );
}
